// src/api/PublicApi.js

/**
 * Performs a live health check against the backend's public health-check endpoint.
 * This is the definitive way to determine if the network and server are available
 * before attempting a full page reload from an offline state.
 *
 * This function is designed to NEVER throw an error. It will always return a
 * structured object indicating success or failure.
 *
 * @returns {Promise<{status: boolean, message: string}>} A promise that resolves to an object.
 *          - { status: true, message: '...' } on a successful (2xx) response.
 *          - { status: false, message: '...' } on any kind of network or server error.
 */
export const apiHealthCheck = async () => {
  try {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
    // Add a cache-busting query parameter to ensure a fresh check every time.
    const healthCheckUrl = `${baseUrl}/public/api/health-check?nocache=${Date.now()}`;

    // Perform the fetch request. No 'mode' is needed as the backend is CORS-enabled.
    const response = await fetch(healthCheckUrl);

    // Explicitly check for a successful HTTP status (200-299).
    // The 'response.ok' property is a convenient shorthand for this.
    if (!response.ok) {
      // This handles cases where the network is up, but the server is down or returning an error.
      const message = `Health check failed with HTTP status: ${response.status}`;
      console.error(`[apiHealthCheck] ${message}`);
      return { 
        status: false, 
        message: `Server is not healthy. Status: ${response.status}` 
      };
    }

    // If we get here, the check was successful.
    console.log("[apiHealthCheck] Health check successful (status 200 OK).");
    return { status: true, message: "Server is healthy." };

  } catch (error) {
    // This 'catch' block handles true network failures (e.g., DNS error, no route to host).
    const message = error.message || "A network error occurred during health check.";
    console.error(`[apiHealthCheck] Health check failed due to a network error: ${message}`);
    return { 
      status: false, 
      message: message 
    };
  }
};